import React, { useEffect, useState } from 'react';
import { Box, Text, Badge } from '@adminjs/design-system';

type Notice = {
  id: number;
  title: string;
  content: string;
  priority: number;
  createdAt: string;
};

// Utility function to convert URLs in text to clickable links
function linkifyText(text: string): React.ReactNode {
  // URL regex pattern - only matches http:// or https:// URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split by newlines to preserve line breaks
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      // Add line break before each line except the first
      result.push(<br key={`br-${lineIndex}`} />);
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let keyCounter = 0;

    // Reset regex for each line
    urlRegex.lastIndex = 0;

    while ((match = urlRegex.exec(line)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index));
      }

      // Process the URL
      const url = match[0];
      let displayUrl = url;

      // Truncate display URL if too long
      if (displayUrl.length > 50) {
        displayUrl = displayUrl.substring(0, 47) + '...';
      }

      // Create clickable link
      parts.push(
        <a
          key={`link-${lineIndex}-${keyCounter++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#3498db',
            textDecoration: 'underline',
            wordBreak: 'break-all',
          }}
        >
          {displayUrl}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text for this line
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }

    // If no URLs found in this line, add the whole line
    if (parts.length === 0) {
      result.push(line);
    } else {
      result.push(...parts);
    }
  });

  return <>{result}</>;
}

function Noticeboard() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/notices')
      .then(res => res.json())
      .then(setNotices)
      .catch((err) => {
        console.error('Error fetching notices:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box mb="xl" p="lg" bg="grey20" style={{ borderRadius: '8px' }}>
        <Text>Loading notices...</Text>
      </Box>
    );
  }

  if (notices.length === 0) {
    return null;
  }

  return (
    <Box
      bg="white"
      p="xl"
      style={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        textAlign: 'center'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 6px 25px rgba(0,0,0,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        mb="lg"
        justifyContent="center"
        style={{
          paddingBottom: '16px',
          marginBottom: '24px'
        }}
      >
        <Text fontSize="h3" fontWeight="bold" color="primary">
          Notice Board
        </Text>
      </Box>

      <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {notices.map((notice) => (
          <Box
            key={notice.id}
            mb="default"
            p="lg"
            bg={notice.priority >= 100 ? 'errorLight' : 'grey20'}
            style={{
              borderRadius: '8px',
              borderLeft: notice.priority >= 100 ? '4px solid #e74c3c' : '4px solid #3498db',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="sm">
              <Text fontSize="h4" fontWeight="bold" color={notice.priority >= 100 ? 'error' : 'text'}>
                {notice.title}
              </Text>
              {notice.priority >= 100 && (
                <Badge variant="danger" size="sm" style={{ padding: '4px 8px' }}>URGENT</Badge>
              )}
            </Box>

            <Text fontSize="default" mb="sm" style={{ whiteSpace: 'pre-line' }}>
              {linkifyText(notice.content)}
            </Text>

            <Text fontSize="sm" color="grey60">
              Posted: {new Date(notice.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default Noticeboard;
